import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../store/authEtablissementStore";
import { getPersonnel, type PersonnelItem } from "../../../api/etablissement";
import { searchPatients } from "../../../api/professionalPatient";
import {
  assignConsultation,
  getAssignedConsultationsForEtablissement,
  isReassignmentEligible,
  reassignConsultation,
  revokeConsultationLink,
  type Consultation,
} from "../../../api/consultations";
import { showToast } from "../../../components/ui/toastHotSonner";
import NipDigitInput from "../../../components/NipDigitInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const PAGE_SIZE = 20;

const TABS = [
  { key: "assignee", label: "Consultations assignées" },
  { key: "directe", label: "Consultations directes" },
] as const;

type Tab = (typeof TABS)[number]["key"];

const TYPE_CONSULTATION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "consultation_generale", label: "Générale" },
  { value: "consultation_specialiste", label: "Spécialiste" },
  { value: "consultation_urgence", label: "Urgence" },
  { value: "consultation_telemedecine", label: "Télémédecine" },
  { value: "consultation_chirurgie", label: "Chirurgie" },
  { value: "consultation_maternite", label: "Maternité" },
  { value: "consultation_pediatrie", label: "Pédiatrie" },
  { value: "consultation_psychiatrie", label: "Psychiatrie" },
];

const STATUT_ASSIGNATION_LABELS: Record<string, { label: string; className: string }> = {
  en_attente: { label: "En attente", className: "bg-amber-100 text-amber-900" },
  acceptee: { label: "Acceptée", className: "bg-[#DCEFE9] text-[#00553E]" },
  refusee: { label: "Refusée", className: "bg-red-100 text-red-700" },
  directe: { label: "Directe", className: "bg-gray-100 text-gray-700" },
};

function formatDateFR(input?: string | null): string {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return d.toLocaleString("fr-FR");
}

type FoundPatient = { nip: string; nom?: string; prenom?: string };

function mapFoundPatient(raw: unknown): FoundPatient | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const nip = String(o.nip ?? o.patientNip ?? "").trim();
  if (!nip) return null;
  return { nip, nom: typeof o.nom === "string" ? o.nom : undefined, prenom: typeof o.prenom === "string" ? o.prenom : undefined };
}

export default function EtablissementConsultations() {
  const { establishment } = useAuth();
  const etablissementId = useMemo(() => establishment?.id?.trim() ?? "", [establishment?.id]);
  const isCabinet = establishment?.type === "cabinet_medical";

  const [tab, setTab] = useState<Tab>("assignee");
  const [items, setItems] = useState<Consultation[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [personnel, setPersonnel] = useState<PersonnelItem[]>([]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignNip, setAssignNip] = useState("");
  const [assignSearching, setAssignSearching] = useState(false);
  const [assignFoundPatient, setAssignFoundPatient] = useState<FoundPatient | null>(null);
  const [assignNotFound, setAssignNotFound] = useState(false);
  const [assignProfessionnelNip, setAssignProfessionnelNip] = useState("");
  const [assignTypeConsultation, setAssignTypeConsultation] = useState(TYPE_CONSULTATION_OPTIONS[0].value);
  const [assignMotif, setAssignMotif] = useState("");
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [reassignTarget, setReassignTarget] = useState<Consultation | null>(null);
  const [reassignNip, setReassignNip] = useState("");
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);

  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!etablissementId) {
      setLoading(false);
      setError("Identifiant établissement indisponible.");
      return;
    }
    setLoading(true);
    setError(null);
    const res = await getAssignedConsultationsForEtablissement(etablissementId, tab, page, PAGE_SIZE);
    if (!res.ok) {
      setError(res.message);
      setLoading(false);
      return;
    }
    setItems(res.data);
    setTotalPages(res.pagination.totalPages || 1);
    setLoading(false);
  }, [etablissementId, tab, page]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!etablissementId) return;
    void (async () => {
      const res = await getPersonnel(etablissementId, 1, 100);
      if (res.ok) setPersonnel(res.data);
    })();
  }, [etablissementId]);

  const openAssignModal = () => {
    setAssignOpen(true);
    setAssignNip("");
    setAssignFoundPatient(null);
    setAssignNotFound(false);
    setAssignProfessionnelNip("");
    setAssignTypeConsultation(TYPE_CONSULTATION_OPTIONS[0].value);
    setAssignMotif("");
    setAssignError(null);
  };

  const searchAssignPatient = async () => {
    const nip = assignNip.trim();
    if (!/^\d{10}$/.test(nip)) {
      setAssignError("Le NIP patient doit comporter exactement 10 chiffres.");
      return;
    }
    setAssignSearching(true);
    setAssignError(null);
    setAssignNotFound(false);
    setAssignFoundPatient(null);
    const res = await searchPatients({ nip });
    setAssignSearching(false);
    if (!res.ok) {
      setAssignError(res.message);
      return;
    }
    const found = res.data.map(mapFoundPatient).find((p) => p?.nip === nip) ?? null;
    if (!found) {
      setAssignNotFound(true);
      return;
    }
    setAssignFoundPatient(found);
  };

  const submitAssign = async () => {
    if (!etablissementId || !assignFoundPatient || !assignProfessionnelNip) return;
    const motif = assignMotif.trim();
    if (!motif) {
      setAssignError("Le motif est requis.");
      return;
    }
    setAssignSubmitting(true);
    setAssignError(null);
    const res = await assignConsultation(etablissementId, {
      patientNip: assignFoundPatient.nip,
      professionnelNip: assignProfessionnelNip,
      typeConsultation: assignTypeConsultation,
      motif,
      date: new Date().toISOString(),
    });
    setAssignSubmitting(false);
    if (!res.ok) {
      setAssignError(res.message);
      return;
    }
    setAssignOpen(false);
    showToast("Consultation assignée avec succès.", "success");
    setTab("assignee");
    setPage(1);
    void load();
  };

  const openReassignModal = (c: Consultation) => {
    setReassignTarget(c);
    setReassignNip("");
    setReassignError(null);
  };

  const submitReassign = async () => {
    if (!etablissementId || !reassignTarget || !reassignNip) return;
    setReassignSubmitting(true);
    setReassignError(null);
    const res = await reassignConsultation(etablissementId, reassignTarget.id, reassignNip);
    setReassignSubmitting(false);
    if (!res.ok) {
      if (res.errorCode === "REASSIGNMENT_NOT_ELIGIBLE") {
        setReassignError("Réassignation possible seulement après refus du professionnel ou 12h sans réponse.");
        return;
      }
      setReassignError(res.message);
      return;
    }
    setReassignTarget(null);
    showToast("Consultation réassignée avec succès.", "success");
    void load();
  };

  const revoke = async (c: Consultation) => {
    if (!etablissementId) return;
    setRevokingId(c.id);
    const res = await revokeConsultationLink(etablissementId, c.id);
    setRevokingId(null);
    if (!res.ok) {
      if (res.errorCode === "CONSULTATION_CABINET_NON_REVOCABLE") {
        showToast("Un cabinet médical ne peut pas révoquer une consultation de son propriétaire.", "error");
      } else {
        showToast(res.message, "error");
      }
      return;
    }
    showToast("Lien à la consultation révoqué.", "success");
    void load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold">Consultations</h1>
          <p className="text-sm text-gray-600 mt-1">
            Assignez une consultation à un membre de votre personnel, ou suivez les consultations rapides ouvertes chez vous.
          </p>
        </div>
        <button
          type="button"
          onClick={openAssignModal}
          className="h-10 px-4 rounded-[10px] font-semibold text-white text-sm shrink-0"
          style={{ background: "#00553E" }}
        >
          Assigner une consultation
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => { setTab(t.key); setPage(1); }}
            className="h-10 rounded-[10px] px-4 text-sm font-medium transition-colors"
            style={{ background: tab === t.key ? "#00553E" : "#E5E7EB", color: tab === t.key ? "#fff" : "#0B1F16" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="py-12 text-center text-gray-600">Chargement…</div>}

      {!loading && error && (
        <div className="rounded-[10px] border p-6 text-center" style={{ borderColor: "#E5E7EB" }}>
          <p className="text-gray-700">{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 h-10 px-4 rounded-[10px] font-medium"
            style={{ backgroundColor: "#00553E", color: "#fff" }}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-[10px] border" style={{ borderColor: "#E5E7EB" }}>
          <div className="hidden md:grid md:grid-cols-12 gap-x-6 px-6 py-4 font-semibold text-gray-700">
            <div className="md:col-span-2">Patient</div>
            <div className="md:col-span-2">Professionnel</div>
            <div className="md:col-span-2">Motif</div>
            <div className="md:col-span-2">Statut</div>
            <div className="md:col-span-2">Date</div>
            <div className="md:col-span-2 text-right">Actions</div>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {items.length === 0 ? (
              <div className="text-gray-500 px-3 py-6 text-center">
                {tab === "assignee" ? "Aucune consultation assignée pour le moment." : "Aucune consultation rapide ouverte chez vous pour le moment."}
              </div>
            ) : (
              items.map((c) => {
                const statutMeta = STATUT_ASSIGNATION_LABELS[c.statutAssignation] ?? STATUT_ASSIGNATION_LABELS.directe;
                const canReassign = tab === "assignee" && isReassignmentEligible(c);
                const canRevoke =
                  tab === "directe" && !isCabinet && !c.dateRevocationEtablissement;
                return (
                  <div
                    key={c.id}
                    className="bg-gray-100 rounded-[10px] p-3 md:px-4 md:py-2 md:min-h-14 flex flex-col gap-2 md:grid md:grid-cols-12 md:gap-x-6 md:items-center"
                  >
                    <div className="md:col-span-2 text-sm text-[#0B1F16] font-medium">NIP {c.patientNip}</div>
                    <div className="md:col-span-2 text-sm text-gray-700">{c.professionnelNip}</div>
                    <div className="md:col-span-2 text-sm text-gray-700 truncate" title={c.motif}>{c.motif}</div>
                    <div className="md:col-span-2">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statutMeta.className}`}>
                        {statutMeta.label}
                      </span>
                      {c.dateRevocationEtablissement && (
                        <span className="ml-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-red-100 text-red-700">
                          Lien révoqué
                        </span>
                      )}
                    </div>
                    <div className="md:col-span-2 text-sm text-gray-700">{formatDateFR(c.dateAssignation ?? c.dateCreation)}</div>
                    <div className="md:col-span-2 flex gap-2 md:justify-end">
                      {canReassign && (
                        <button
                          type="button"
                          onClick={() => openReassignModal(c)}
                          className="h-9 px-3 rounded-[10px] font-semibold text-white text-sm"
                          style={{ background: "#00553E" }}
                        >
                          Réassigner
                        </button>
                      )}
                      {canRevoke && (
                        <button
                          type="button"
                          disabled={revokingId === c.id}
                          onClick={() => void revoke(c)}
                          className="h-9 px-3 rounded-[10px] border text-sm text-red-600 disabled:opacity-60"
                          style={{ borderColor: "#F3C0C0" }}
                        >
                          {revokingId === c.id ? "Révocation…" : "Révoquer"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t text-sm" style={{ borderColor: "#E5E7EB" }}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                ← Précédent
              </button>
              <span className="text-gray-500">Page {page} / {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                Suivant →
              </button>
            </div>
          )}
        </div>
      )}

      {assignOpen && (
        <div className="fixed inset-0 z-20">
          <div className="absolute inset-0 bg-black/40" onClick={() => { if (!assignSubmitting) setAssignOpen(false); }} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-[12px] p-6 w-full max-w-lg shadow max-h-[90vh] overflow-y-auto">
              <div className="text-lg font-semibold text-[#0B1F16]">Assigner une consultation</div>

              <div className="mt-4 space-y-2">
                <label className="text-sm font-medium text-[#0B1F16]">NIP patient</label>
                <div className="flex flex-wrap items-center gap-3">
                  <NipDigitInput
                    value={assignNip}
                    onChange={(digits) => { setAssignNip(digits); setAssignFoundPatient(null); setAssignNotFound(false); }}
                    disabled={assignSubmitting}
                    invalid={assignNip.length > 0 && assignNip.length < 10}
                    boxClassName="h-10 w-10 text-center rounded-[8px] border px-1 text-sm text-[#0B1F16] outline-none focus:ring-2 focus:ring-[#00553E]/30 disabled:opacity-60"
                    validColor="#E5E7EB"
                  />
                  <button
                    type="button"
                    onClick={() => void searchAssignPatient()}
                    disabled={assignSearching || assignSubmitting || assignNip.length !== 10}
                    className="h-10 px-4 rounded-[8px] font-semibold text-white text-sm disabled:opacity-60 shrink-0"
                    style={{ background: "#00553E" }}
                  >
                    {assignSearching ? "Recherche…" : "Rechercher"}
                  </button>
                </div>
                {assignNotFound && (
                  <p className="text-xs text-amber-700 mt-1">Aucun patient trouvé avec ce NIP.</p>
                )}
                {assignFoundPatient && (
                  <p className="text-xs text-[#00553E] mt-1 font-medium">
                    Patient trouvé{assignFoundPatient.nom ? ` : ${assignFoundPatient.prenom ?? ""} ${assignFoundPatient.nom}` : ""}.
                  </p>
                )}
              </div>

              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium text-[#0B1F16]">Professionnel</label>
                <Select
                  value={assignProfessionnelNip || undefined}
                  onValueChange={setAssignProfessionnelNip}
                  disabled={assignSubmitting}
                >
                  <SelectTrigger className="w-full h-10 rounded-[8px] border px-3" style={{ borderColor: "#E5E7EB" }}>
                    <SelectValue placeholder="Sélectionner un membre du personnel" />
                  </SelectTrigger>
                  <SelectContent>
                    {personnel.map((p) => (
                      <SelectItem key={p.liaison.id} value={p.professionnel.nip}>
                        {p.professionnel.nom} {p.professionnel.prenom} — NIP {p.professionnel.nip}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium text-[#0B1F16]">Type de consultation</label>
                <Select
                  value={assignTypeConsultation}
                  onValueChange={setAssignTypeConsultation}
                  disabled={assignSubmitting}
                >
                  <SelectTrigger className="w-full h-10 rounded-[8px] border px-3" style={{ borderColor: "#E5E7EB" }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_CONSULTATION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium text-[#0B1F16]">Motif</label>
                <textarea
                  value={assignMotif}
                  onChange={(e) => setAssignMotif(e.target.value)}
                  placeholder="Ex. douleur abdominale"
                  className="w-full min-h-[80px] rounded-[10px] border px-3 pt-2 text-sm"
                  style={{ borderColor: "#E5E7EB" }}
                  disabled={assignSubmitting}
                />
              </div>

              {assignError && (
                <div className="mt-3 rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {assignError}
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                  disabled={assignSubmitting || !assignFoundPatient || !assignProfessionnelNip || !assignMotif.trim()}
                  onClick={() => void submitAssign()}
                >
                  {assignSubmitting ? "Assignation…" : "Assigner"}
                </button>
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold border"
                  style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                  onClick={() => setAssignOpen(false)}
                  disabled={assignSubmitting}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {reassignTarget && (
        <div className="fixed inset-0 z-20">
          <div className="absolute inset-0 bg-black/40" onClick={() => { if (!reassignSubmitting) setReassignTarget(null); }} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-[12px] p-6 w-full max-w-md shadow">
              <div className="text-lg font-semibold text-[#0B1F16]">
                Réassigner la consultation de NIP {reassignTarget.patientNip}
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Choisissez un autre membre de votre personnel — l'assignation précédente ({reassignTarget.professionnelNip}) sera remplacée.
              </p>

              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium text-[#0B1F16]">Nouveau professionnel</label>
                <Select
                  value={reassignNip || undefined}
                  onValueChange={setReassignNip}
                  disabled={reassignSubmitting}
                >
                  <SelectTrigger className="w-full h-10 rounded-[8px] border px-3" style={{ borderColor: "#E5E7EB" }}>
                    <SelectValue placeholder="Sélectionner un membre du personnel" />
                  </SelectTrigger>
                  <SelectContent>
                    {personnel
                      .filter((p) => p.professionnel.nip !== reassignTarget.professionnelNip)
                      .map((p) => (
                        <SelectItem key={p.liaison.id} value={p.professionnel.nip}>
                          {p.professionnel.nom} {p.professionnel.prenom} — NIP {p.professionnel.nip}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {reassignError && (
                <div className="mt-2 rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {reassignError}
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                  disabled={reassignSubmitting || !reassignNip}
                  onClick={() => void submitReassign()}
                >
                  {reassignSubmitting ? "Réassignation…" : "Confirmer"}
                </button>
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold border"
                  style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                  onClick={() => setReassignTarget(null)}
                  disabled={reassignSubmitting}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
